import {
	Button,
	Divider,
	Form,
	Header,
	Modal,
	Segment,
} from '@mairistem/composants';
import { Date } from '@mairistem/finances-utils';
import React from 'react';
import useModalSuperUserValidation from '../../../../../hooks/useModalSuperUserValidation';
import type BordereauActionType from '../../../../../interfaces/pieces/bordereaux/BordereauActionType';
import type { BordereauWithState } from '../../../../../interfaces/pieces/TreeRow';

interface ChangeData extends BordereauWithState {
	ordre: number;
	gereDate: number;
	actionType?: BordereauActionType;
}

interface BordereauNumeroterModalProps {
	onChange: (data: ChangeData) => void;
	onClose: VoidFunction;
	visible: boolean;
	isSuperUser: boolean;
	bordereau: BordereauWithState;
	isArchive: boolean;
	nombreJourAffichage: number;
}

const BordereauNumeroterModal = ({
	onChange,
	onClose,
	visible,
	isSuperUser,
	bordereau,
	isArchive,
	nombreJourAffichage,
}: BordereauNumeroterModalProps) => {
	const { isValidated, validationComponent } = useModalSuperUserValidation();

	const [gereDate, setGereDate] = React.useState<number>(1);
	const [ordre, setOrdre] = React.useState<number>(1);
	const [dateEmission, setDateEmission] = React.useState<string>();

	const handleClick = () => {
		onChange({
			...bordereau,
			ordre,
			gereDate,
			...(gereDate === 3 && { dateEmission }),
		});
		onClose();
	};

	const handleChangeDate = (_e: any, { value }: any): void => {
		setGereDate(Number(value));
	};

	const handleChangeDateEmission = (value: string): void => {
		setDateEmission(value);
	};

	const handleChangeTri = (_e: any, { value }: any): void => {
		setOrdre(Number(value));
	};

	return (
		<Modal
			closeIcon
			open={visible}
			onClose={onClose}
			data-testid="bordereauNumeroterModal"
		>
			<Modal.Header>
				{isArchive
					? `Archiver ce bordereau de rejet n°${bordereau.numero}`
					: `Numéroter le bordereau n°${bordereau.numero}`}
			</Modal.Header>
			<Modal.Content>
				<p>
					{isArchive
						? `Cette action clôt le traitement du rejet en attribuant 
          une numérotation définitive à ce bordereau et aux écritures liées. 
          Aucune transmission à la DGFiP à prévoir : ce bordereau restera visible ${nombreJourAffichage} jours 
          dans la page d’ordonnancement.`
						: `Cette action attribue une numérotation définitive à ce bordereau et 
        aux écritures liées en vue d'être prêt à transmettre au comptable public dans 
        un second temps.`}
				</p>
				<div>
					<Header as="h4" attached="top">
						Date à appliquer
					</Header>
					<Segment attached>
						<Form>
							<Form.Radio
								label="Appliquer la date du jour au bordereau et aux pièces"
								value="1"
								checked={gereDate === 1}
								onChange={handleChangeDate}
								data-testid="bordereauNumeroterModalDate1"
							/>
							<Form.Radio
								label="Appliquer la date du bordereau à toutes les pièces"
								value="2"
								checked={gereDate === 2}
								onChange={handleChangeDate}
								data-testid="bordereauNumeroterModalDate2"
							/>
							<Form.Group inline>
								<Form.Radio
									label="Appliquer cette date au bordereau et aux pièces"
									name="date"
									value="3"
									checked={gereDate === 3}
									onChange={handleChangeDate}
									data-testid="bordereauNumeroterModalDate3"
								/>
								<Form.Input
									control={Date}
									onChange={handleChangeDateEmission}
									value={dateEmission}
									disabled={gereDate !== 3}
									name="dateEmission"
									data-testid="bordereauNumeroterModalDateCustom"
								/>
							</Form.Group>
							<Form.Radio
								label="Conserver les dates"
								name="date"
								value="4"
								onChange={handleChangeDate}
								checked={gereDate === 4}
								data-testid="bordereauNumeroterModalDateConserver"
							/>
						</Form>
					</Segment>
				</div>
				<Divider hidden />
				<div>
					<Header as="h4" attached="top">
						Tri des écritures
					</Header>
					<Segment attached>
						<Form>
							<Form.Radio
								label="Conserver l'ordre actuel"
								name="tri"
								value="1"
								checked={ordre === 1}
								onChange={handleChangeTri}
								data-testid="bordereauNumeroterModalTri1"
							/>
							<Form.Radio
								label="Triées par Tiers"
								name="tri"
								value="2"
								checked={ordre === 2}
								onChange={handleChangeTri}
								data-testid="bordereauNumeroterModalTri2"
							/>
							<Form.Radio
								label="Triées par Imputation"
								name="tri"
								value="3"
								checked={ordre === 3}
								onChange={handleChangeTri}
								data-testid="bordereauNumeroterModalTri3"
							/>
						</Form>
					</Segment>
				</div>
				{isSuperUser && validationComponent}
			</Modal.Content>
			<Modal.Actions>
				<Button onClick={onClose} data-testid="bordereauNumeroterModalCancel">
					Annuler
				</Button>
				<Button
					primary
					onClick={handleClick}
					disabled={isSuperUser && !isValidated}
					data-testid="bordereauNumeroterModalConfirm"
				>
					Confirmer
				</Button>
			</Modal.Actions>
		</Modal>
	);
};

export default BordereauNumeroterModal;
